// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — screens, input and the net glue. The rules, the search and the geometry all live
// elsewhere and are pure; this file is the only one that touches the DOM or the wire.
//
// THE NETCODE IN ONE PARAGRAPH. Bracken is LOCKSTEP with no authority at all, because there is
// nothing to be authoritative about: no randomness, no hidden information, and a move list that
// fully determines the position. Every peer broadcasts THE ENTIRE PLY LOG on every move (about two
// bytes a ply — a whole game fits in a couple of hundred). A dropped message is therefore a
// non-event, a late joiner is instantly in sync, and a peer that receives a longer log simply
// replays it through its OWN generator: if the replay fails, the sender is running different code
// and we say so, rather than diverging quietly.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { MODE_IDS, isShared, modeOf, type ModeId } from './modes';
import { Game, plyOfMove, retirePly } from './game';
import { SKILLS, SKILL_IDS, chooseMove, skillOf, type Skill } from './search';
import { createView, type View } from './render';
import { nearestHedge, nearestStep, needsConfirm } from './layout';
import { colourOf, glyphOf } from './palette';
import { runCountdown } from './countdown';
import type { Cell, Hedge } from './rules';

const SLUG = 'bracken';
const store = createStore(SLUG);
const sfx = createSfx({ muted: store.get('muted', false) as boolean });

const app = document.querySelector<HTMLElement>('#app')!;
app.innerHTML = `
  <main class="main-content" id="content"></main>
  <footer class="site-footer">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> ·
    <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
  </footer>`;
const content = app.querySelector<HTMLElement>('#content')!;

// ---------------------------------------------------------------- app state
let modeChoice: ModeId = modeOf(store.get('mode', 'heath')).id;
let skillChoice = skillOf(store.get('skill', 'poacher')).id;
let hostOptsMode: ModeId = 'heath';
// A default handle that DIFFERS PER DEVICE — two players both called "Runner" is a lobby you
// cannot read. resolveName also consumes any `?n=` on the way in, and it must run at BOOT, before
// anything builds an invite link, or that param would ride along and rename whoever accepts.
const playerName = resolveName(store, () => `Runner ${100 + Math.floor(Math.random() * 900)}`);

let game: Game | null = null;
let view: View | null = null;
let selfSeat = 0;
let planting = false;
let ghost: Hedge | null = null;
/** A step that has been aimed at but not yet confirmed — only used when two legal steps sit
 *  closer together than a thumb can reliably separate. See layout.needsConfirm. */
let pendingStep: Cell | null = null;
let botTimer: ReturnType<typeof setTimeout> | null = null;
let botThinking = false;

let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let roomCode = '';
let seats: Array<{ id: string; name: string }> = [];
let sendLog: ((m: { p: number[] }) => void) | null = null;
/** Detach the room's `log` channel. Room-scoped, so it is only ever called from leaveRoom. */
let logOff: (() => void) | null = null;
let deepLinkUsed = false;
let lobby: { destroy(): void; repaint(): void } | null = null;
let mismatch = false;

const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  while (cleanups.length) {
    try {
      cleanups.pop()!();
    } catch {
      /* a failed teardown must never block the next screen */
    }
  }
  if (botTimer) clearTimeout(botTimer);
  botTimer = null;
  botThinking = false;
  view?.destroy();
  view = null;
}

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

function nameOf(seat: number): string {
  if (seats[seat]) return seats[seat].name;
  return seat === selfSeat ? 'You' : `Runner ${seat + 1}`;
}

// ---------------------------------------------------------------- menu
function showMenu(): void {
  teardown();
  setPlaying(false);
  const m = modeOf(modeChoice);
  content.innerHTML = `
    <section class="screen menu">
      <h1 class="title">bracken</h1>
      <p class="tagline">Race across the common while you close the ways through it — but you may never seal one off entirely.</p>

      <div class="chips" id="modes" role="group" aria-label="Common">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === modeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeChoice}">${esc(modeOf(id).name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb" id="blurb">${esc(m.blurb)}</p>

      <div class="chips" id="skills" role="group" aria-label="Opponent">
        ${SKILL_IDS.map(
          (id) =>
            `<button class="chip small${id === skillChoice ? ' on' : ''}" data-skill="${id}" aria-pressed="${id === skillChoice}">${esc(SKILLS[id].name)}</button>`,
        ).join('')}
      </div>

      <button class="btn primary" id="solo">Play solo</button>
      <button class="btn" id="friends">Play with friends</button>
      <div class="row">
        <button class="btn link" id="howto">How to play</button>
        <button class="btn link" id="about">About</button>
        <button class="btn link" id="mute">${sfx.muted() ? 'Sound off' : 'Sound on'}</button>
      </div>
    </section>`;

  content.querySelectorAll<HTMLElement>('#modes .chip').forEach((b) =>
    b.addEventListener('click', () => {
      modeChoice = modeOf(b.dataset.mode).id;
      store.set('mode', modeChoice);
      sfx.play('blip');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('#skills .chip').forEach((b) =>
    b.addEventListener('click', () => {
      skillChoice = skillOf(b.dataset.skill).id;
      store.set('skill', skillChoice);
      sfx.play('blip');
      showMenu();
    }),
  );
  content.querySelector('#solo')!.addEventListener('click', () => {
    sfx.unlock();
    startSolo();
  });
  content.querySelector('#friends')!.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  content.querySelector('#howto')!.addEventListener('click', () => showModal('howto'));
  content.querySelector('#about')!.addEventListener('click', () => showModal('about'));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get('seen-howto', false)) {
    store.set('seen-howto', true);
    showModal('howto');
  }
}

// ---------------------------------------------------------------- modals
const HOWTO = `
  <p><strong>Get to the far side first.</strong> Your goal is the coloured edge opposite you.</p>
  <p>On your turn you do exactly one of two things: <strong>walk</strong> one square, or <strong>plant a hedge</strong> from your supply to make somebody else's route longer.</p>
  <p>A hedge blocks two squares' worth of the way through. You may lengthen a route as much as you like — but you may <strong>never</strong> plant one that leaves anybody with no route at all, so the game won't even offer you that hedge.</p>
  <p>Walking into a rival? You hop straight over them.</p>`;

const ABOUT = `
  <p><strong>bracken</strong> is a racing-and-blocking abstract for two to four. Everything you see is drawn from the rules — there are no images and no fonts to download, and it works offline.</p>
  <p>Playing with friends is <strong>peer-to-peer</strong>: your moves go straight between browsers over WebRTC. There is no game server and nothing about your game is stored anywhere. A public signalling relay is used only to introduce the two browsers to each other, and connecting exchanges IP addresses with the people you are playing, as any direct connection does.</p>
  <p>Page views are counted anonymously with Cloudflare Web Analytics. No cookies, no tracking, no accounts.</p>`;

function showModal(which: 'howto' | 'about'): void {
  const wrap = document.createElement('div');
  wrap.className = 'modal-back';
  wrap.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${which === 'howto' ? 'How to play' : 'About'}">
      <h2>${which === 'howto' ? 'How to play' : 'About bracken'}</h2>
      ${which === 'howto' ? HOWTO : ABOUT}
      <button class="btn" data-close>Got it</button>
    </div>`;
  document.body.appendChild(wrap);

  const close = (): void => {
    wrap.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  wrap.querySelector('[data-close]')!.addEventListener('click', close);
  // A tap anywhere off the panel closes it. pointerdown covers mouse, touch and pen at once, and
  // the guard is what stops the panel's own taps from closing it.
  wrap.addEventListener('pointerdown', (e) => {
    if (!(e.target as HTMLElement).closest('.modal')) close();
  });
  document.addEventListener('keydown', onKey);
}

// ---------------------------------------------------------------- the race
function startSolo(): void {
  teardown();
  seats = [];
  selfSeat = 0;
  mismatch = false;
  game = new Game(modeChoice, 2);
  showGame();
  runCountdown(content, sfx, () => {
    paint();
    maybeBot();
  });
}

function showGame(): void {
  setPlaying(true);
  content.innerHTML = `
    <section class="screen game">
      <header class="hud" id="hud"></header>
      <div class="stage" id="stage"><div id="board"></div></div>
      <p class="prompt" id="prompt"></p>
      <div class="controls" id="controls"></div>
    </section>`;

  const boardEl = content.querySelector<HTMLElement>('#board')!;
  view = createView(boardEl, game!.size);

  // Resize is driven by real events AND a retry, because a tab that is not laid out yet measures
  // 0 and a board sized from 0 is a blank page.
  const doResize = (): void => {
    if (!view?.resize()) setTimeout(doResize, 120);
    else paint();
  };
  window.addEventListener('resize', doResize);
  window.addEventListener('orientationchange', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => doResize()) : null;
  ro?.observe(content.querySelector<HTMLElement>('#stage')!);
  onCleanup(() => {
    window.removeEventListener('resize', doResize);
    window.removeEventListener('orientationchange', doResize);
    ro?.disconnect();
  });

  boardEl.addEventListener('pointerdown', onBoardPointer);
  onCleanup(() => boardEl.removeEventListener('pointerdown', onBoardPointer));

  requestAnimationFrame(doResize);
  setTimeout(doResize, 60);
  doResize();
}

function myTurn(): boolean {
  const g = game;
  if (!g || g.over || mismatch) return false;
  if (seats.length === 0) return g.turn === selfSeat; // solo: seat 0 is the human
  return g.turn === selfSeat;
}

function onBoardPointer(e: PointerEvent): void {
  const g = game;
  if (!g || !view || !myTurn()) return;
  const { x, y } = view.toBoard(e.clientX, e.clientY);
  const l = view.layout();

  if (planting) {
    const h = nearestHedge(l, g.legalHedges(), x, y);
    if (h !== null && h !== ghost) {
      ghost = h;
      sfx.play('blip', { gain: 0.5 });
      paint();
    }
    return;
  }
  // Nearest legal step wins. Usually that is a target roughly two cells wide, because the steps
  // around a pawn are two cells apart — but the jump-and-bend rule can put two legal steps one
  // cell apart, which at 375px on a 9x9 measures 38px. In THAT case only, aim first and confirm.
  const steps = g.steps();
  const to = nearestStep(l, steps, x, y, l.cell * 1.6);
  if (to === null) return;
  if (needsConfirm(l, steps, to)) {
    if (pendingStep === to) return; // already aimed here — the button is the way to commit
    pendingStep = to;
    sfx.play('blip', { gain: 0.5 });
    paint();
    return;
  }
  pendingStep = null;
  commit({ kind: 'step', to });
}

function commit(move: { kind: 'step'; to: Cell } | { kind: 'hedge'; hedge: Hedge }): void {
  const g = game;
  if (!g || !myTurn()) return;
  const ply = plyOfMove(move);
  if (!g.applyPly(ply)) return;
  sfx.play(move.kind === 'hedge' ? 'hit' : 'select');
  planting = false;
  ghost = null;
  pendingStep = null;
  broadcast();
  paint();
  if (g.over) return void finish();
  maybeBot();
}

/** The bot moves off the critical path: a timeout, so the board has painted the player's move
 *  before the search starts and the UI never appears to hang mid-tap. */
function maybeBot(): void {
  const g = game;
  if (!g || g.over || seats.length > 0 || botThinking) return;
  if (g.turn === selfSeat) return;
  botThinking = true;
  paint();
  botTimer = setTimeout(() => {
    botTimer = null;
    if (!game || game.over) {
      botThinking = false;
      return;
    }
    const skill: Skill = skillOf(skillChoice);
    const chosen = chooseMove(game.state, skill);
    botThinking = false;
    if (chosen && game.play(chosen.move)) {
      sfx.play(chosen.move.kind === 'hedge' ? 'hit' : 'blip');
      paint();
      if (game.over) return void finish();
    }
    paint();
  }, 220);
}

function paint(): void {
  const g = game;
  if (!g || !view) return;
  const mine = myTurn();
  view.paint(g, {
    steps: mine && !planting ? g.steps() : [],
    ghost,
    planting,
    self: selfSeat,
    aimed: pendingStep,
  });
  paintHud();
  paintControls();
}

function paintHud(): void {
  const g = game;
  const hud = content.querySelector<HTMLElement>('#hud');
  if (!g || !hud) return;
  // In a shared-stack mode the supply is ONE number, so it is shown once as a stack rather than
  // repeated against every runner — printing "12 hedges" on each row reads as twelve each and
  // hides the entire point of the mode.
  const shared = isShared(g.mode);
  const supply = (seat: number): string => {
    const n = g.hedgesLeft(seat);
    return `${n} hedge${n === 1 ? '' : 's'}`;
  };
  hud.innerHTML = `
    <div class="runners">
      ${g.state.runners
        .map((_, seat) => {
          const to = g.toGo(seat);
          return `<div class="runner${seat === g.turn && !g.over ? ' active' : ''}${g.retired(seat) ? ' gone' : ''}">
            <span class="pip" style="background:${colourOf(seat)}">${glyphOf(seat)}</span>
            <span class="rname">${esc(nameOf(seat))}</span>
            <span class="rstat">${Number.isFinite(to) ? `${to} to go` : '—'}${shared ? '' : ` · ${supply(seat)}`}</span>
          </div>`;
        })
        .join('')}
      ${shared ? `<div class="runner stack active"><span class="pip stackpip">≡</span><span class="rname">Shared stack</span><span class="rstat">${supply(0)} left</span></div>` : ''}
    </div>
    <button class="btn tiny" id="quit">Quit</button>`;
  hud.querySelector('#quit')!.addEventListener('click', () => (seats.length ? backToLobby() : showMenu()));
}

function paintControls(): void {
  const g = game;
  const el = content.querySelector<HTMLElement>('#controls');
  const prompt = content.querySelector<HTMLElement>('#prompt');
  if (!g || !el || !prompt) return;

  if (mismatch) {
    prompt.textContent = 'This browser is running a different build — the race cannot be kept in step.';
    el.innerHTML = '';
    return;
  }
  if (g.over) {
    prompt.textContent = '';
    el.innerHTML = '';
    return;
  }
  if (!myTurn()) {
    prompt.textContent = botThinking ? 'Thinking…' : `${nameOf(g.turn)} to move.`;
    el.innerHTML = '';
    return;
  }

  const canPlant = g.hedgesLeft(selfSeat) > 0 && g.legalHedges().length > 0;
  if (!planting) {
    if (pendingStep !== null) {
      // Two legal steps too close to separate by thumb — so this one asks before it moves.
      prompt.textContent = 'Two ways through here. Walk to the marked square?';
      el.innerHTML = `<button class="btn primary" id="go">Walk here</button><button class="btn" id="nope">Pick another</button>`;
      el.querySelector('#go')!.addEventListener('click', () => {
        const to = pendingStep;
        pendingStep = null;
        if (to !== null) commit({ kind: 'step', to });
      });
      el.querySelector('#nope')!.addEventListener('click', () => {
        pendingStep = null;
        paint();
      });
      return;
    }
    prompt.textContent = 'Tap a marker to walk.';
    el.innerHTML = `<button class="btn" id="plant"${canPlant ? '' : ' disabled'}>Plant a hedge${canPlant ? '' : ' (none left)'}</button>`;
    el.querySelector('#plant')?.addEventListener('click', () => {
      planting = true;
      pendingStep = null;
      ghost = g.legalHedges()[0] ?? null;
      sfx.play('blip');
      paint();
    });
    return;
  }

  prompt.textContent = ghost === null ? 'No hedge can go there.' : 'Tap the common to aim, then plant it.';
  el.innerHTML = `
    <button class="btn" id="flip">Turn it</button>
    <button class="btn primary" id="confirm"${ghost === null ? ' disabled' : ''}>Plant it</button>
    <button class="btn" id="cancel">Walk instead</button>`;
  el.querySelector('#flip')!.addEventListener('click', () => {
    if (ghost === null) return;
    // The same post, the other way round — and only if that one is legal too.
    const other = ghost ^ 1;
    if (g.legalHedges().includes(other)) {
      ghost = other;
      sfx.play('blip');
      paint();
    }
  });
  el.querySelector('#confirm')!.addEventListener('click', () => {
    if (ghost !== null) commit({ kind: 'hedge', hedge: ghost });
  });
  el.querySelector('#cancel')!.addEventListener('click', () => {
    planting = false;
    ghost = null;
    paint();
  });
}

// ---------------------------------------------------------------- results
function finish(): void {
  const g = game;
  if (!g) return;
  setPlaying(false);
  sfx.play(g.winner === selfSeat ? 'win' : 'lose');
  const standings = g.standings();

  content.innerHTML = `
    <section class="screen results">
      <h2>${g.stalled ? 'The race stalled' : g.winner === selfSeat ? 'You got home first' : `${esc(nameOf(g.winner))} got home first`}</h2>
      <table class="standings">
        <thead><tr><th>Runner</th><th>Left to go</th><th>Steps</th><th>Hedges planted</th></tr></thead>
        <tbody>
          ${standings
            .map(
              (s) => `<tr class="${s.seat === g.winner ? 'won' : ''}">
                <td><span class="pip" style="background:${colourOf(s.seat)}">${glyphOf(s.seat)}</span> ${esc(nameOf(s.seat))}${g.retired(s.seat) ? ' (left)' : ''}</td>
                <td>${s.seat === g.winner ? 'home' : Number.isFinite(s.toGo) ? s.toGo : '—'}</td>
                <td>${s.steps}</td>
                <td>${s.planted}</td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
      <div class="row" id="after"></div>
      <button class="btn link results-feedback">Tell me what you think</button>
    </section>`;

  const after = content.querySelector<HTMLElement>('#after')!;
  if (seats.length > 0) {
    after.innerHTML = `<div id="lobby-slot"></div>`;
    showLobby(content.querySelector<HTMLElement>('#lobby-slot')!);
  } else {
    after.innerHTML = `<button class="btn primary" id="again">Race again</button><button class="btn" id="menu">Menu</button>`;
    after.querySelector('#again')!.addEventListener('click', startSolo);
    after.querySelector('#menu')!.addEventListener('click', showMenu);
  }
  content.querySelector('.results-feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
}

// ---------------------------------------------------------------- multiplayer
function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  const deep = deepLinkUsed ? null : new URL(location.href).searchParams.get('room');
  if (deep) {
    deepLinkUsed = true;
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) return void enterRoom(code, false);
  }
  const entry = createRoomEntry({
    container: content,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Two to four runners on the same common. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  game = null;
  seats = [];
  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        // A seat that has gone is retired through the LOG, by the host only, so every peer applies
        // the identical ply at the identical point. Never inferred locally on each peer.
        const seat = seats.findIndex((s) => s.id === id);
        if (game && !game.over && seat >= 0 && n.isHost() && n.hostSettled() && !game.retired(seat)) {
          if (game.retireSeat(seat) !== null) {
            broadcast();
            paint();
            if (game.over) finish();
          }
        }
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: modeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const send = n.channel<{ p: number[] }>('log', (data) => onRemoteLog(data));
  sendLog = (m) => send(m);
  // NOT onCleanup(). The cleanup list is PER SCREEN and teardown() runs on every screen change —
  // including startNetRound — so registering the channel there detaches the receiver at the exact
  // moment the round begins, and every move is sent into a void. The channel belongs to the ROOM,
  // so it is torn down only in leaveRoom.
  logOff = () => send.off();

  showLobby();
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  mismatch = false;
  planting = false;
  ghost = null;
  pendingStep = null;
  const mode = modeOf(info.opts?.mode);
  seats = info.players.map((p) => ({ id: p.id, name: p.name }));
  selfSeat = seats.findIndex((s) => s.id === net?.selfId);

  if (!info.seated || selfSeat < 0) {
    // A peer that connected mid-round is not in the frozen roster: show the race, not a dead
    // screen, and let the lobby put them in the next one.
    selfSeat = -1;
  }
  game = new Game(mode, Math.max(2, Math.min(4, seats.length)));
  showGame();
  runCountdown(content, sfx, () => {
    paint();
  });
}

function broadcast(): void {
  // The WHOLE log, every time. ~2 bytes a ply, and it makes a dropped message a non-event.
  if (game && sendLog) sendLog({ p: game.plies.slice() });
}

function onRemoteLog(data: { p: number[] }): void {
  const g = game;
  if (!g || !Array.isArray(data?.p)) return;
  const theirs = data.p.filter((x) => Number.isInteger(x) && x >= 0);
  if (theirs.length <= g.plies.length) return;
  // Replay their log through OUR generator. If it does not apply, they are running different
  // code — say so rather than diverging quietly.
  const rebuilt = Game.replay(g.mode, g.state.runners.length, theirs);
  if (!rebuilt) {
    mismatch = true;
    paint();
    return;
  }
  game = rebuilt;
  planting = false;
  ghost = null;
  pendingStep = null;
  sfx.play('blip', { gain: 0.6 });
  paint();
  if (rebuilt.over) return void finish();
}

function repaintLobby(): void {
  lobby?.repaint();
}

function backToLobby(): void {
  teardown();
  setPlaying(false);
  game = null;
  showLobby();
}

function showLobby(host?: HTMLElement): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return showMenu();
  setPlaying(false);
  if (!host) {
    content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  }
  const container = host ?? content.querySelector<HTMLElement>('#lobby-host')!;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 4,
    onCancel: () => void leaveRoom(),
    modeSlot: () =>
      isHost()
        ? `<div class="lobby-modes" id="lm">${MODE_IDS.map(
            (id) =>
              `<button class="chip mode-btn${id === modeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeChoice}">${esc(modeOf(id).name)}</button>`,
          ).join('')}<p class="mode-blurb">${esc(modeOf(modeChoice).blurb)}</p></div>`
        : `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${esc(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`,
    onModeMount: () => {
      if (!isHost()) return;
      container.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          modeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', modeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  const n = net;
  net = null;
  rounds = null;
  logOff?.();
  logOff = null;
  sendLog = null;
  seats = [];
  game = null;
  // Await the real teardown: Trystero defers it, and coming back to a room that is still dying is
  // the leave/rejoin trap.
  if (n) await n.leave();
  showMenu();
}

// ---------------------------------------------------------------- boot
hardenViewport();
window.addEventListener('beforeunload', () => void net?.leave());
showMenu();

// A synchronous hook so the browser pass can drive a race to its end without depending on rAF,
// which never fires in a background tab.
(window as unknown as { __bracken: unknown }).__bracken = {
  state: () => game,
  step: () => {
    if (!game || game.over) return false;
    const chosen = chooseMove(game.state, SKILLS.rambler);
    if (!chosen) return false;
    const ok = game.play(chosen.move);
    if (ok) {
      broadcast();
      paint();
      if (game.over) finish();
    }
    return ok;
  },
  retire: (seat: number) => {
    if (!game) return false;
    const ok = game.applyPly(retirePly(seat)) ;
    if (ok) {
      paint();
      if (game.over) finish();
    }
    return ok;
  },
};
